import moment from "moment";
import React, { memo, useCallback, useState } from "react";
import DateTimePicker from "react-datetime-picker";
import { loggedInUser } from "src/apollo/cache";
import { AppErrorText, AppText, FlexDiv } from "src/Components/UI";
import { PhoenixCheckbox, PhoenixDatePicker, PhoenixTextArea } from "src/Components/UI/Phoenix";
import { CustomFieldBase, OptionItem } from "src/types/genericTypes";
import { theme } from "src/utils/theme";
import { NewSelectField } from "../NewSelectField";
import { MultiDropdownField } from "./MultiDropdownField";
import { MultiTextField } from "./MultiTextField";
import { DateTimePickerWrapper, EditTextInput, FieldWrapper, TitleLabel } from "./shared";

type CustomFieldProps = {
  customField: CustomFieldBase;
  disabled?: boolean;
  fieldName: string;
  index: number;
  label?: string;
  setFieldValue?: (field: string, value: any) => void;

  fieldError?: string;
};

const CustomFieldComponent = ({
  customField,
  setFieldValue,
  fieldName,
  label,
  index,
  disabled,
  fieldError,
}: CustomFieldProps) => {
  const [focused, setFocused] = useState(false);

  const handleDateChange = useCallback(
    (date: moment.Moment | null) => {
      if (!date) {
        console.log("invalid date");
        return;
      }
      const finalDate = date.format("MM/DD/YYYY");
      setFieldValue?.(`${fieldName}[${index}].value`, finalDate);
    },
    [setFieldValue, fieldName, index],
  );

  if (customField.type === "Rate" || customField.type === "Percentage" || customField.type === "Number") {
    return (
      <FieldWrapper key={customField?.id}>
        <TitleLabel>{label ?? customField.key}</TitleLabel>
        <EditTextInput
          name={`${fieldName}[${index}].num_value`}
          percentage={customField.type === "Percentage"}
          disabled={disabled ?? !customField.allow_reps_to_edit}
          value={customField.num_value}
        />
        {fieldError && <AppErrorText>{fieldError}</AppErrorText>}
      </FieldWrapper>
    );
  }

  if (customField.type === "Boolean") {
    return (
      <FlexDiv key={customField?.id} gap={8} style={{ marginBottom: 16, marginTop: 16 }}>
        <PhoenixCheckbox
          checked={!!customField?.boo_value}
          disabled={disabled ?? !customField.allow_reps_to_edit}
          onChange={() => {
            setFieldValue?.(`${fieldName}[${index}].boo_value`, !customField?.boo_value);
          }}
        />
        <AppText fontSize={12} fontWeight={500} lineHeight={18}>
          {label ?? customField.key}
        </AppText>
        {fieldError && <AppErrorText>{fieldError}</AppErrorText>}
      </FlexDiv>
    );
  }

  if (customField.type === "Date") {
    const dateValue = typeof customField?.value === "string" ? customField.value : undefined;
    return (
      <FieldWrapper key={customField?.id} style={{ marginBottom: 16 }}>
        <TitleLabel>{label ?? customField.key}</TitleLabel>
        <PhoenixDatePicker
          id={`date-${customField.id}`}
          disabled={disabled ?? !customField.allow_reps_to_edit}
          date={dateValue ? moment(dateValue) : null}
          onDateChange={handleDateChange}
          focused={focused}
          onFocusChange={({ focused }) => setFocused(focused)}
          numberOfMonths={1}
          showDefaultInputIcon
          inputIconPosition="after"
          isOutsideRange={() => false}
        />
        {fieldError && <AppErrorText>{fieldError}</AppErrorText>}
      </FieldWrapper>
    );
  }

  if (customField.type === "DateTime") {
    return (
      <FieldWrapper key={customField?.id} style={{ marginBottom: 16 }}>
        <TitleLabel>{label ?? customField.key}</TitleLabel>
        <DateTimePickerWrapper>
          <DateTimePicker
            disabled={disabled ?? !customField.allow_reps_to_edit}
            format="MM/dd/yyyy h:mm a"
            onChange={(date: Date | null) => {
              if (!date) {
                setFieldValue?.(`${fieldName}[${index}].date_value`, null);
                return;
              }
              const dateMoment = moment(date);
              const userTimezone = loggedInUser()?.timezone ?? "America/New_York";
              const finalDate = dateMoment.tz(userTimezone).toISOString();
              setFieldValue?.(`${fieldName}[${index}].date_value`, finalDate);
            }}
            value={customField?.date_value ? moment(customField.date_value).toDate() : undefined}
          />
        </DateTimePickerWrapper>
        {fieldError && <AppErrorText>{fieldError}</AppErrorText>}
      </FieldWrapper>
    );
  }

  if (customField.type === "MultiDropdown") {
    return (
      <MultiDropdownField
        cf_key={customField?.key}
        disabled={disabled ?? !customField.allow_reps_to_edit}
        fieldName={fieldName}
        id={customField?.id}
        index={index}
        key={customField?.id}
        label={label}
        options={customField?.options?.map((item) => ({ label: item, value: item })) || []}
        setFieldValue={setFieldValue}
        value={customField?.list_value as OptionItem[]}
      />
    );
  }

  if (customField.type === "MultiText") {
    return (
      <MultiTextField
        id={customField?.id}
        cf_key={customField?.key}
        disabled={disabled ?? !customField.allow_reps_to_edit}
        fieldName={fieldName}
        key={customField?.id}
        index={index}
        setFieldValue={setFieldValue}
        value={customField?.list_value as string[]}
        label={label}
      />
    );
  }

  if (customField.type === "BigText") {
    return (
      <FieldWrapper key={customField?.id}>
        <TitleLabel>{label ?? customField.key}</TitleLabel>
        <PhoenixTextArea
          name={`${fieldName}[${index}].value`}
          value={customField?.value as string}
          onChange={(e) => {
            setFieldValue?.(`${fieldName}[${index}].value`, e.target.value);
          }}
          style={{ borderColor: theme.NEUTRAL300, cursor: "default" }}
          ableToResize={false}
          height={160}
          disabled={disabled ?? !customField.allow_reps_to_edit}
        />
        {fieldError && <AppErrorText>{fieldError}</AppErrorText>}
      </FieldWrapper>
    );
  }

  if (customField.type !== "Dropdown") {
    return (
      <FieldWrapper key={customField?.id}>
        <TitleLabel>{label ?? customField.key}</TitleLabel>
        <EditTextInput
          name={`${fieldName}[${index}].value`}
          disabled={disabled ?? !customField.allow_reps_to_edit}
          value={customField?.value as string}
        />
        {fieldError && <AppErrorText>{fieldError}</AppErrorText>}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper key={customField?.id}>
      <TitleLabel>{label ?? customField.key}</TitleLabel>
      <NewSelectField
        cf_key={customField?.key}
        disabled={disabled ?? !customField.allow_reps_to_edit}
        id={customField?.id}
        name={`${fieldName}[${index}].value`}
        options={customField?.options?.map((item) => ({ label: item, value: item })) || []}
        value={customField?.value as string}
        error={fieldError}
        onChange={(value) => {
          setFieldValue?.(`${fieldName}[${index}].value`, value);
        }}
      />
    </FieldWrapper>
  );
};

export const CustomField = memo(CustomFieldComponent);
